import { getNextStaticProps } from "@faustjs/next";
import { client, PageIdType } from "../client";
import { GetStaticPropsContext } from "next";

import { useMenu } from "hooks";
import Layout from "layouts/main";
import {
	FullBackgroundHero,
	Struggle,
	ProductSlideshowSection,
	SocialProofSection,
	DifferentiatorsSection,
	RiskReversal,
	HowWeWork,
	CTASection,
} from "components";
import {
	Cog8ToothIcon as CogIcon,
	BoltIcon,
	CurrencyDollarIcon,
	EyeIcon,
} from "@heroicons/react/24/outline";
import HeadingBlock from "components/atoms/blocks/HeadingBlock";

export default function Page() {
	const { useQuery } = client;
	const { usePage } = client;
	const headerLinks = useMenu("PRIMARY");
	const footerLinks = useMenu("FOOTER");
	// Using a dummy ID or existing homepage ID just to satisfy the hook if needed,
	// or we could point to a specific page if it existed in WP.
	// For now I'll use the root or a dummy one, aiming for '/' to be safe or maybe '/agencies'
	const page = usePage({
		id: `/agencies`,
		idType: PageIdType.URI,
	});

	return (
		<Layout
			page={page}
			headerLinks={headerLinks}
			footerLinks={footerLinks}
			transparentHeader
			lightLogo
		>
			<FullBackgroundHero
				backgroundImage="/images/crafted-office-background.jpg"
				headline="Your Creative Vision,<br />Built to Perfection"
				bracketedText="Built to Perfection"
				bracketAnimateColors={true}
				subheadline="We help creative agencies deliver pixel-perfect websites and digital experiences. We deliver on time, on budget, and without the technical drama."
				ctaText="Let's Team Up"
				ctaIcon={null}
			/>

			<Struggle />

			<ProductSlideshowSection />

			<HowWeWork />

			<SocialProofSection
				showCaseStudy={false}
				sectionTitle="We Make Agencies Like Yours Look Brilliant"
				testimonial={{
					quote:
						"Crafted operates like a world-class internal team. Fast, thoughtful, reliable — and every deliverable just feels right.",
					title: "Director of Digital Experience",
					company: "USA Football",
				}}
				logos={[
					{ src: "/images/studio-science-logo-1.svg", alt: "Studio Science" },
					{ src: "/images/element-three.webp", alt: "Element Three" },
					{ src: "/images/well-done.webp", alt: "Well Done" },
					{ src: "/images/cvr.webp", alt: "CVR" },
					{ src: "/images/borshoff.webp", alt: "Borshoff" },
				]}
			/>

			<RiskReversal />

			<DifferentiatorsSection
				title="Why Agencies Choose Crafted Again and Again"
				bgColor="bg-white"
				rows={[
					[
						{
							title: "Creative Empathy:",
							description: "The final site looks like your Figma file - no compromises.",
							bgColor: "bg-blue/10",
						},
						{
							title: "Strategic Depth:",
							description: "We guide complex builds and help you say \"yes\" more often.",
							bgColor: "bg-green/10",
						},
					],
					[
						{
							title: "Process Precision:",
							description: "QA, ticketing, shared workflows—zero surprises.",
							bgColor: "bg-pink/10",
						},
						{
							title: "True Partnership:",
							description: "Proactive, responsive, and committed to making your work shine.",
							bgColor: "bg-yellow/10",
						},
					],
				]}
			/>

			<CTASection
				images={[
					{ src: "/slide-show/For Agencies/Crafted1.jpg", alt: "Crafted Project 1" },
					{ src: "/slide-show/For Agencies/Crafted2.jpg", alt: "Crafted Project 2" },
					{ src: "/slide-show/For Agencies/Crafted3.jpg", alt: "Crafted Project 3" },
					{ src: "/slide-show/For Agencies/borshoffMobile.jpg", alt: "Borshoff Mobile" },
					{ src: "/slide-show/For Agencies/boshoffDesktop.jpg", alt: "Borshoff Desktop" },
					{ src: "/slide-show/For Agencies/imiDesktop.jpg", alt: "IMI Desktop" },
					{ src: "/slide-show/For Agencies/imiMobile.jpg", alt: "IMI Mobile" },
					{ src: "/slide-show/For Agencies/ptrDesktop.jpg", alt: "PTR Desktop" },
					{ src: "/slide-show/For Agencies/ptrMobile.jpg", alt: "PTR Mobile" },
					{ src: "/slide-show/For Agencies/zooDesktop.jpg", alt: "Zoo Desktop" },
					{ src: "/slide-show/For Agencies/zooMobile.jpg", alt: "Zoo Mobile" },
				]}
			/>
		</Layout>
	);
}

export async function getStaticProps(context: GetStaticPropsContext) {
	return getNextStaticProps(context, {
		Page,
		client,
	});
}
